import { createContext, useContext, useMemo, type ReactNode } from "react";

interface ConnectedMatchLockValue {
  active: boolean;
  currentMatchId: string | null;
  lockedFieldLabels: string[];
  lockedInputAriaLabels: string[];
  lockedInputPlaceholders: string[];
  lockedSelectLabels: string[];
  suppressedPlaceholders: string[];
}

const EMPTY: ConnectedMatchLockValue = {
  active: false,
  currentMatchId: null,
  lockedFieldLabels: [],
  lockedInputAriaLabels: [],
  lockedInputPlaceholders: [],
  lockedSelectLabels: [],
  suppressedPlaceholders: [],
};

const ConnectedMatchLockContext = createContext<ConnectedMatchLockValue>(EMPTY);

interface ProviderProps extends Omit<ConnectedMatchLockValue, "active"> {
  active: boolean;
  children: ReactNode;
}

export function ConnectedMatchLockProvider({
  active,
  currentMatchId,
  lockedFieldLabels,
  lockedInputAriaLabels,
  lockedInputPlaceholders,
  lockedSelectLabels,
  suppressedPlaceholders,
  children,
}: ProviderProps) {
  const value = useMemo<ConnectedMatchLockValue>(
    () => ({
      active,
      currentMatchId,
      lockedFieldLabels,
      lockedInputAriaLabels,
      lockedInputPlaceholders,
      lockedSelectLabels,
      suppressedPlaceholders,
    }),
    [
      active,
      currentMatchId,
      lockedFieldLabels,
      lockedInputAriaLabels,
      lockedInputPlaceholders,
      lockedSelectLabels,
      suppressedPlaceholders,
    ],
  );

  return <ConnectedMatchLockContext.Provider value={value}>{children}</ConnectedMatchLockContext.Provider>;
}

export function useConnectedMatchLock() {
  return useContext(ConnectedMatchLockContext);
}
